import { useCallback, useMemo } from "react";
import {
  CreatableOptionPicker,
  type CreatableOption,
  type EntitySelectorVariant,
  type Omittable,
} from "@salestrak/ui";

import { useIssueSourceMutations } from "#app/hooks/settings/use-issue-source-mutations";
import { useAppData } from "#app/lib/data";
import type { IssueSourceRow } from "#app/lib/data/entities/rows";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { usePermissionVerdict } from "#app/lib/hooks/use-permission";
import { SelectionEntityTrigger } from "./selection-entity-trigger";
import { type SelectorSize } from "./sizes";
import { selectionToIssueSourceEntity } from "./utils";

// ---------------------------------------------------------------------------
// IssueSourceSelector — the Source property's picker over the workspace's one
// source pool, on the creatable-vocabulary grammar the job-title picker set:
// search-or-add field, a live Create row for a typed name, and a hover "…"
// editor on the rows the viewer may manage.
//
// What the viewer may do from here is the reading of ONE role cell,
// issue_sources.manage:
//   yes         — create; rename/delete every workspace-added source. Sources
//                 SalesTrak provides are fixed vocabulary here (the catalog
//                 page still disables them).
//   if_created  — create; rename the sources this viewer added. No delete —
//                 removing a source strips attribution from every item that
//                 carries it, so that stays with the catalog tier.
//   no          — pick only.
// The server enforces the same table (createIssueSource's allowIfCreated
// guard, the resolver's creator-or-manager gates), so the affordances shown
// are exactly the writes that succeed.
//
// The per-kind default source and usage policy are catalog settings, not
// picker acts — they live on the Sources settings page. The host names the
// default of the kind it renders for (`defaultSourceId`); the picker only
// captions that row, since the pool itself has no default.
// ---------------------------------------------------------------------------

const MANAGE_PERM = "issue_sources.manage";

export type IssueSourceSelectorProps = Omittable<{
  value?: string | null;
  onValueChange?: (issueSourceId: string) => void;
  placeholder?: string;
  emptyTextVariant?: React.ComponentProps<typeof SelectionEntityTrigger>["emptyTextVariant"];
  size?: SelectorSize;
  disabled?: boolean;
  /** Render a static, non-dimmed value (no picker) — view/comment tier. */
  readOnly?: boolean;
  /** The default source of the kind this picker edits (from
   *  `useIssueSourceSettings`). Its row wears a "Default" caption. */
  defaultSourceId?: string | null;
  /** `inline` mounts the list without a trigger or popup of its own, for a
   *  host that already owns the popup (a bulk action's body). */
  variant?: EntitySelectorVariant;
}>;

function toOption(source: IssueSourceRow, isDefault: boolean): CreatableOption {
  return {
    id: source.issueSourceId,
    label: source.name,
    ...(isDefault ? { description: "Default" } : {}),
  };
}

export function IssueSourceSelector({
  value,
  onValueChange,
  placeholder = "Select source...",
  emptyTextVariant,
  size,
  disabled,
  readOnly,
  defaultSourceId,
  variant,
}: IssueSourceSelectorProps) {
  const { issueSources, issueSourcesKnown } = useWorkspaceData();
  const { currentUser } = useAppData();
  const verdict = usePermissionVerdict(MANAGE_PERM);
  const { createIssueSource, updateIssueSource, deleteIssueSource } =
    useIssueSourceMutations();

  const canCreate = verdict === "yes" || verdict === "if_created";

  // The two populations the picker draws: fixed rows and the rows this viewer
  // manages. Archived sources are out of the vocabulary entirely; a stored
  // value pointing at one shows nothing selected, which is the truth of it.
  const { fixed, manageable } = useMemo(() => {
    const viewerId = currentUser?.userId ?? null;
    const manages = (source: IssueSourceRow): boolean => {
      if (verdict === "yes") return !source.systemKey;
      if (verdict === "if_created") {
        return viewerId !== null && source.createdBy === viewerId;
      }
      return false;
    };
    const fixed: CreatableOption[] = [];
    const manageable: CreatableOption[] = [];
    for (const source of issueSources) {
      if (source.archived) continue;
      (manages(source) ? manageable : fixed).push(
        toOption(source, source.issueSourceId === defaultSourceId),
      );
    }
    return { fixed, manageable };
  }, [currentUser?.userId, defaultSourceId, issueSources, verdict]);

  const handleCreate = useCallback(
    async (label: string) => {
      const created = await createIssueSource(label);
      if (created) onValueChange?.(created.id);
    },
    [createIssueSource, onValueChange],
  );

  const handleRename = useCallback(
    (id: string, label: string) => {
      void updateIssueSource(id, label);
    },
    [updateIssueSource],
  );

  const handleDelete = useCallback(
    (id: string) => {
      const source = issueSources.find((row) => row.issueSourceId === id);
      if (source) void deleteIssueSource(source);
    },
    [deleteIssueSource, issueSources],
  );

  return (
    <CreatableOptionPicker
      options={fixed}
      manageableOptions={manageable}
      selectedId={value ?? null}
      onPick={(option) => onValueChange?.(option.id)}
      onCreate={canCreate ? handleCreate : undefined}
      onRename={manageable.length > 0 ? handleRename : undefined}
      onDelete={verdict === "yes" ? handleDelete : undefined}
      noun="Source"
      searchPlaceholder={canCreate ? "Search or add a source..." : "Search sources..."}
      loading={!issueSourcesKnown}
      variant={variant}
      trigger={
        <SelectionEntityTrigger
          toEntity={selectionToIssueSourceEntity}
          placeholder={placeholder}
          emptyTextVariant={emptyTextVariant}
          title="Source"
          size={size}
          disabled={disabled}
          readOnly={readOnly}
        />
      }
    />
  );
}
